import React, { useRef, useState } from 'react'

import {
  Box,
  Icon,
  Prose,
  Tooltip,
  useClassNames,
  useToggle,
} from '@factorialco/deprecated-design-system'

import type { Attachment } from 'modules/core/components/TetherComponent/types'
import { useEffect } from 'modules/core/hooks/useEffect'

import './index.styles.css'

const DEBOUNCE_TIMER_MILLIS = 200

const stylesRefs = {
  Disclosure_container: 'Disclosure_container',
  Disclosure_arrow: 'Disclosure_arrow',
  top: 'Disclosure_top',
  bottom: 'Disclosure_bottom',
  left: 'Disclosure_left',
  right: 'Disclosure_right',
  small: 'Disclosure_small',
  big: 'Disclosure_big',
  normal: 'Disclosure_normal',
}

type Placement = 'top' | 'bottom' | 'left' | 'right'
type Size = React.ComponentProps<typeof Tooltip>['size']

/**
 * Used to configure the attachment and offset of the tooltip.
 */
interface AttachmentVars {
  attachment: Attachment
  targetAttachment: Attachment
  arrowSide: Placement
  offset: string
}

interface Props {
  placement?: Placement
  /**
   * Used to provide the accessible shorthand label for the tooltip, prior to
   * its contents being shown. Full accessibility not yet avaialable.
   *
   * @example `Process explanation`
   */
  ariaLabel?: string
  /**
   * The text to display in the tooltip. Markdown supported.
   */
  tooltipText?: string
  size?: Size
}

/**
 * A tooltip that auto-reveals and dismisses on hover. It also
 * supports Markdown in its text prop, and renders any JSX children provided.
 */
const Disclosure: React.FC<React.PropsWithChildren<Props>> = ({
  placement = 'top',
  tooltipText: description,
  size,
  ariaLabel,
  children,
}) => {
  const cx = useClassNames(stylesRefs)
  const [shouldOpen, toggle] = useToggle(false)
  const [isOpen, setIsOpen] = useState(false)

  const wrapperRef = useRef<HTMLElement | null>(null)

  const { targetAttachment, attachment, arrowSide, offset } = configureAttachment(placement, size)

  useEffect(() => {
    const dialog: HTMLElement | null = wrapperRef.current
    if (!dialog) return

    // HACK: Remove the overflow: hidden from tooltip container to show arrow
    const parent: HTMLElement | null = dialog.parentElement
    if (parent) {
      parent.style.overflow = 'visible'
    }
  }, [isOpen])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setIsOpen(shouldOpen)
    }, DEBOUNCE_TIMER_MILLIS)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [shouldOpen])

  return (
    <Tooltip
      pin
      {...{
        toggle,
        open: isOpen,
        attachment,
        targetAttachment,
        offset,
        size,
      }}
      type="brand"
      aria-label={ariaLabel}
    >
      <Box className={'Disclosure_iconWrapper'}>
        <Icon.Medium icon="question-circle-line" color="darker400" />
      </Box>
      <Box ref={wrapperRef} className={cx('Disclosure_container', arrowSide)} position="relative">
        {description && (
          <Prose markdown color="textInverse">
            {description}
          </Prose>
        )}
        {children}
        <div
          className={cx('Disclosure_arrow', arrowSide, size && size !== 'auto' ? size : undefined)}
        ></div>
      </Box>
    </Tooltip>
  )
}

function configureAttachment(placement: Placement, size?: Size): AttachmentVars {
  const offsetMagnitude = offsetBySize(size)

  switch (placement) {
    case 'top':
      return configureTop(offsetMagnitude)
    case 'bottom':
      return congigureBottom(offsetMagnitude)
    case 'left':
      return configureLeft(offsetMagnitude)
    case 'right':
      return configureRight(offsetMagnitude)
    default:
      return configureTop(offsetMagnitude)
  }
}

function offsetBySize(size: Size) {
  let offset: string
  switch (size) {
    case 'small':
      offset = '16px'
      break
    case 'big':
      offset = '16px'
      break
    case 'normal':
      offset = '12px'
      break
    default:
      offset = '0px'
      break
  }
  return offset
}

function configureTop(offset: string): AttachmentVars {
  return {
    attachment: 'bottom center',
    targetAttachment: 'top center',
    arrowSide: 'bottom',
    offset: `-${offset} 0`,
  }
}

function congigureBottom(offset: string): AttachmentVars {
  return {
    attachment: 'top center',
    targetAttachment: 'bottom center',
    arrowSide: 'top',
    offset: `${offset} 0`,
  }
}

function configureLeft(offset: string): AttachmentVars {
  return {
    attachment: 'middle right',
    targetAttachment: 'middle left',
    arrowSide: 'right',
    offset: `0 -${offset}`,
  }
}

function configureRight(offset: string): AttachmentVars {
  return {
    attachment: 'middle left',
    targetAttachment: 'middle right',
    arrowSide: 'left',
    offset: `0 ${offset}`,
  }
}

export default Disclosure
